"use client"

import { useEffect, useState } from "react"
import { useParams, useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { NewsletterSubscribe } from "@/components/NewsletterSubscribe"
import { Badge } from "@/components/ui/badge"
import { Calendar, Clock, ArrowLeft, Share2, Bookmark, MessageSquare, ThumbsUp, Linkedin, Facebook } from "lucide-react"
import { useLanguage } from "../../../contexts/language-context"
import { useTranslations } from "next-intl"
import Header from "../../../components/header"
import Footer from "../../../components/footer"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

/**
 * Moved blogPosts outside the component so it's a stable reference
 * and won't trigger react-hooks/exhaustive-deps warnings.
 */
const blogPosts = [
  {
    id: "1",
    title: "The 'Digital Twin': How We're Turning Abstract Data into Interactive 3D Web Applications",
    titleEs: "El 'Gemelo Digital': Cómo Estamos Convirtiendo Datos Abstractos en Aplicaciones Web 3D Interactivas",
    excerpt:
      "The web is evolving from flat 2D pages to immersive 3D experiences. Learn how the 'Digital Twin' concept is solving high-value problems for manufacturing, real estate, and research by blending full-stack development, data architecture, and 3D visualization.",
    excerptEs:
      "La web está evolucionando de páginas 2D planas a experiencias 3D inmersivas. Descubre cómo el concepto de 'Gemelo Digital' está resolviendo problemas de alto valor para la manufactura, el sector inmobiliario y la investigación al fusionar desarrollo full-stack, arquitectura de datos y visualización 3D.",
    content: `
      <blockquote><p>For 20 years, the web has been flat. The next generation of business tools is immersive, interactive, and built in 3D.</p></blockquote>
      <p>For decades, we’ve experienced the web through flat 2D pages. We read text, we look at static images, we fill out forms. It’s functional, but it’s a pale imitation of the real world.</p>
      <p>Now, a new generation of web applications is emerging. This new web is immersive, interactive, and three-dimensional. At the heart of this evolution is a concept that has moved from science fiction to a practical business tool. According to a <a href="https://www.marketsandmarkets.com/Market-Reports/digital-twin-market-225269522.html" target="_blank" rel="noopener noreferrer">2025 report from MarketsandMarkets</a>, the global <strong>Digital Twin</strong> market is projected to grow from <strong>USD 21.14 billion in 2025 to USD 149.81 billion by 2030</strong>.</p>
      <p>It's not just a model; it's a living simulation—a real-time, dynamic, digital replica of a physical object, process, or even a complex system.</p>
      <p>Building these applications requires more than just a web developer. It demands someone who can architect a robust data backend, code a high-performance full-stack application, and render complex 3D visualizations seamlessly in a browser.</p>
      <p>As a Digital Solutions Architect, this is the intersection where I build. Here are three ways digital twins are solving high-value problems for my clients today.</p>
      <h3 id="manufacturers-configurator">1. For Manufacturers: The Interactive Product Configurator</h3>
      <p><strong>The Problem:</strong> A 2D photo gallery fails miserably when you sell a complex, customizable product. A customer can't truly understand the scale, finish, or optional features of a piece of high-end machinery, custom furniture, or a new vehicle from a handful of static JPEGs.</p>
      <p><strong>The Solution:</strong> We build a "digital twin" of the product itself. Using technologies like Next.js for a lightning-fast web interface and 3D modeling tools like Blender, we create real-time <strong>3D Product Configurators</strong>.</p>
      <p>Customers can spin the product 360 degrees. They can zoom in on material textures. They can swap components, change colors, and add features, seeing their choices reflected instantly. This isn't just "shopping"; it's an interactive experience that builds buyer confidence and dramatically reduces costly returns.</p>
      <h3 id="real-estate-portal">2. For Real Estate: The Immersive Pre-Sale Portal</h3>
      <p><strong>The Problem:</strong> How do you sell "intangible luxury"? A real estate developer selling a multi-million dollar property that doesn't exist yet faces a huge challenge. Flat blueprints and artistic renderings fail to create the emotional connection needed to close a high-value pre-sale.</p>
      <p><strong>The Solution:</strong> We build a "digital twin" of the building. These <strong>Immersive Pre-Sale Portals</strong> allow prospective buyers to virtually walk through a property from any web browser on earth.</p>
      <p>They can stand on the penthouse balcony and see the actual, data-driven view at sunset. They can walk from the bedroom to the kitchen to understand the flow. They can even test different furniture layouts. We move the sale from an abstract blueprint to a tangible, emotional experience, enabling developers to secure commitments long before a shovel ever hits the ground.</p>
      <h3 id="research-accelerator">3. For Researchers & Academia: The Data Visualization Accelerator</h3>
      <p><strong>The Problem:</strong> Scientists and researchers are often drowning in massive, abstract datasets. A complex financial model, a map of a neural network, or a new molecular structure is incomprehensible when trapped in a spreadsheet with millions of rows. Critical insights remain hidden.</p>
      <p><strong>The Solution:</strong> We create a "digital twin" of the data itself. This is where robust data architecture is paramount. Using Python, scalable databases, and 3D visualization libraries, we build <strong>Data Visualization Accelerators</strong> that turn abstract numbers into interactive, queryable 3D models.</p>
      <p>A researcher can "fly through" a 3D model of a protein, watching how it folds in real-time. A data scientist can manipulate a 3D graph of a complex financial model. We make the invisible visible, allowing researchers to discover patterns and make their work reproducible in a way a static 2D chart never could.</p>
      <h2 id="conclusion">Conclusion: You Don't Need a 3D Artist. You Need an Architect.</h2>
      <p>These three applications—the configurator, the portal, and the accelerator—are all forms of a Digital Twin. They represent the future of web development, which lies at the intersection of three distinct disciplines:</p>
      <ul>
        <li><strong>Full-Stack Logic:</strong> To build the application.</li>
        <li><strong>Data Architecture:</strong> To manage the complex information.</li>
        <li><strong>3D Visualization:</strong> To make it interactive and human.</li>
      </ul>
      <p>A typical full-stack developer can't render a complex 3D scene. A typical 3D artist can't architect a scalable database. As a <strong>Digital Solutions Architect</strong>, I am uniquely qualified to bridge this gap and build these next-generation applications.</p>
      <p>The flat web is evolving. If your business is ready to move beyond the page and build something truly immersive, <a href="/en#case-studies" target="_self"><strong>explore my case studies</strong></a> to see how these solutions come to life.</p>
    `,
    contentEs: `
      <blockquote><p>Durante 20 años, la web ha sido plana. La próxima generación de herramientas de negocio es inmersiva, interactiva y construida en 3D.</p></blockquote>
      <p>Durante décadas, hemos experimentado la web a través de páginas 2D planas. Leemos texto, miramos imágenes estáticas, rellenamos formularios. Es funcional, pero es una pálida imitación del mundo real.</p>
      <p>Ahora, está surgiendo una nueva generación de aplicaciones web. Esta nueva web es inmersiva, interactiva y tridimensional. En el corazón de esta evolución se encuentra un concepto que ha pasado de la ciencia ficción a una herramienta de negocio práctica. Según un <a href="https://www.marketsandmarkets.com/Market-Reports/digital-twin-market-225269522.html" target="_blank" rel="noopener noreferrer">informe de 2025 de MarketsandMarkets</a>, se proyecta que el mercado global de <strong>Gemelos Digitales</strong> crezca de <strong>USD 21.14 mil millones en 2025 a USD 149.81 mil millones para 2030</strong>.</p>
      <p>No es solo un modelo; es una simulación viva: una réplica digital dinámica y en tiempo real de un objeto físico, proceso o incluso un sistema complejo.</p>
      <p>Construir estas aplicaciones requiere más que un simple desarrollador web. Exige a alguien que pueda diseñar una arquitectura de datos robusta, programar una aplicación <em>full-stack</em> de alto rendimiento y renderizar visualizaciones 3D complejas de forma fluida en un navegador.</p>
      <p>Como Arquitecto de Soluciones Digitales, esta es la intersección en la que construyo. Aquí hay tres formas en que los gemelos digitales están resolviendo problemas de alto valor para mis clientes hoy en día.</p>
      <h3 id="manufacturers-configurator">1. Para Fabricantes: El Configurador de Productos Interactivo</h3>
      <p><strong>El Problema:</strong> Una galería de fotos 2D fracasa estrepitosamente cuando vendes un producto complejo y personalizable. Un cliente no puede comprender realmente la escala, el acabado o las características opcionales de una pieza de maquinaria de alta gama, muebles a medida o un vehículo nuevo a partir de un puñado de JPEGs estáticos.</p>
      <p><strong>La Solución:</strong> Construimos un "gemelo digital" del producto mismo. Usando tecnologías como Next.js para una interfaz web ultrarrápida y herramientas de modelado 3D como Blender, creamos <strong>Configuradores de Productos 3D</strong> en tiempo real.</p>
      <p>Los clientes pueden girar el producto 360 grados. Pueden hacer zoom en las texturas del material. Pueden intercambiar componentes, cambiar colores y agregar características, viendo sus elecciones reflejadas al instante. Esto no es solo "comprar"; es una experiencia interactiva que genera confianza en el comprador y reduce drásticamente las costosas devoluciones.</p>
      <h3 id="real-estate-portal">2. Para el Sector Inmobiliario: El Portal Inmersivo de Preventa</h3>
      <p><strong>El Problema:</strong> ¿Cómo se vende el "lujo intangible"? Un desarrollador inmobiliario que vende una propiedad multimillonaria que aún no existe se enfrenta a un desafío enorme. Los planos y <em>renders</em> artísticos no logran crear la conexión emocional necesaria para cerrar una preventa de alto valor.</p>
      <p><strong>La Solución:</strong> Construimos un "gemelo digital" del edificio. Estos <strong>Portales Inmersivos de Preventa</strong> permiten a los posibles compradores recorrer virtualmente una propiedad desde cualquier navegador web del mundo.</p>
      <p>Pueden pararse en el balcón del <em>penthouse</em> y ver la vista real, basada en datos, al atardecer. Pueden caminar desde el dormitorio hasta la cocina para entender la distribución. Incluso pueden probar diferentes disposiciones de muebles. Movemos la venta de un plano abstracto a una experiencia tangible y emocional, permitiendo a los desarrolladores asegurar compromisos mucho antes de que la construcción comience.</p>
      <h3 id="research-accelerator">3. Para Investigadores y Académicos: El Acelerador de Visualización de Datos</h3>
      <p><strong>El Problema:</strong> Los científicos e investigadores a menudo están ahogados en enormes y abstractos conjuntos de datos. Un modelo financiero complejo, un mapa de una red neuronal o una nueva estructura molecular son incomprensibles cuando están atrapados en una hoja de cálculo con millones de filas. Los <em>insights</em> críticos permanecen ocultos.</p>
      <p><strong>La Solución:</strong> Creamos un "gemelo digital" de los propios datos. Aquí es donde una arquitectura de datos robusta es primordial. Usando Python, bases de datos escalables y bibliotecas de visualización 3D, construimos <strong>Aceleradores de Visualización de Datos</strong> que convierten números abstractos en modelos 3D interactivos y consultables.</p>
      <p>Un investigador puede "volar a través" de un modelo 3D de una proteína, viendo cómo se pliega en tiempo real. Un científico de datos puede manipular un gráfico 3D de un modelo financiero complejo. Hacemos visible lo invisible, permitiendo a los investigadores descubrir patrones y hacer que su trabajo sea reproducible en una manera que un gráfico 2D estático jamás podría.</p>
      <h2 id="conclusion">Conclusión: No Necesitas un Artista 3D. Necesitas un Arquitecto.</h2>
      <p>Estas tres aplicaciones —el configurador, el portal y el acelerador— son todas formas de un Gemelo Digital. Representan el futuro del desarrollo web, que se encuentra en la intersección de tres disciplinas distintas:</p>
      <ul>
        <li><strong>Lógica Full-Stack:</strong> Para construir la aplicación.</li>
        <li><strong>Arquitectura de Datos:</strong> Para gestionar la información compleja.</li>
        <li><strong>Visualización 3D:</strong> Para hacerla interactiva y humana.</li>
      </ul>
      <p>Un desarrollador <em>full-stack</em> típico no puede renderizar una escena 3D compleja. Un artista 3D típico no puede diseñar una base de datos escalable. Como <strong>Arquitecto de Soluciones Digitales</strong>, estoy calificado de manera única para cerrar esta brecha y construir estas aplicaciones de próxima generación.</p>
      <p>La web plana está evolucionando. Si tu negocio está listo para ir más allá de la página y construir algo verdaderamente inmersivo, <a href="/es#case-studies" target="_self"><strong>explora mis casos de estudio</strong></a> para ver cómo estas soluciones cobran vida.</p>
    `,
    category: "3D Visualization",
    author: "David X Avila",
    authorBio: "A Digital Solutions Architect specializing in the intersection of full-stack development, 3D visualization, and data architecture to build next-generation web applications.",
    authorBioEs: "Un Arquitecto de Soluciones Digitales especializado en la intersección del desarrollo full-stack, la visualización 3D y la arquitectura de datos para construir aplicaciones web de próxima generación.",
    date: "2025-10-23",
    readTime: "5 min read",
    image: "/images/blog/digital-twin-architect.jpg",
    tags: [
      "Digital Twin",
      "3D Visualization",
      "Full-Stack Development",
      "Data Architecture",
      "3D Product Configurator",
      "Real Estate Tech",
      "Immersive Web"
    ],
    gradient: "from-gray-700 via-gray-900 to-black",
    views: 0,
    likes: 0,
    comments: 0,
    slug: "digital-twin-abstract-data-3d-web-apps",
    relatedPosts: [],
  },
  {
    id: "9",
    title: "The Orchestrator Protocol: System Architecture for 2026",
    titleEs: "El Protocolo Orquestador: Arquitectura de Sistema para 2026",
    excerpt: "An in-depth look at the Orchestrator Protocol and design principles for building resilient, scalable systems in 2026.",
    excerptEs: "Resumen sobre el Protocolo Orquestador y principios de diseño para sistemas escalables y resilientes en 2026.",
    content: `
      <p>Content coming soon. This article will cover the Orchestrator Protocol's design goals, components, and recommended deployment patterns for 2026.</p>
      <p>— Placeholder content until final article is provided.</p>
    `,
    contentEs: `
      <p>Contenido próximamente. Este artículo cubrirá los objetivos de diseño del Protocolo Orquestador, sus componentes y patrones de despliegue recomendados para 2026.</p>
      <p>— Contenido de marcador de posición hasta que se proporcione el artículo final.</p>
    `,
    category: "Systems",
    author: "David X Avila",
    authorBio: "A Digital Solutions Architect specialized in distributed systems and protocol design.",
    authorBioEs: "Un Arquitecto de Soluciones Digitales especializado en sistemas distribuidos y diseño de protocolos.",
    date: "2026-02-05",
    readTime: "7 min read",
    image: "/images/blog/featured/orchestrator-protocol-system-architecture-en.jpg",
    tags: ["Distributed Systems", "Protocol Design"],
    gradient: "from-slate-700 via-slate-900 to-black",
    views: 0,
    likes: 0,
    comments: 0,
    slug: "orchestrator-protocol",
    relatedPosts: [],
  },
  {
    id: "2",
    title: "Creating Photorealistic 3D Renders: A Complete Guide",
    titleEs: "Creando Renders 3D Fotorrealistas: Una Guía Completa",
    excerpt:
      "Learn the essential techniques and tools for creating stunning photorealistic 3D visualizations that captivate clients and audiences.",
    excerptEs:
      "Aprende las técnicas y herramientas esenciales para crear impresionantes visualizaciones 3D fotorrealistas que cautiven a clientes y audiencias.",
    category: "3D Design",
    author: "David X. Avila",
    date: "2024-01-10",
    readTime: "8 min read",
    image: "/placeholder.svg?height=300&width=500&text=3D+Rendering+Guide",
    tags: ["Blender", "V-Ray", "3D Modeling", "Rendering"],
    gradient: "from-teal-500 to-green-500",
    views: 980,
    slug: "photorealistic-3d-renders-complete-guide",
    draft: true,
  },
  // Other posts...
]

export default function BlogPostPage() {
  const params = useParams() as any
  const slug = params?.slug
  const router = useRouter()
  const { t, language } = useLanguage()
  const blogT = useTranslations('blog')
  // Prefer the route locale (stable across SSR/CSR); fall back to context
  const routeLocale = (params?.locale as string) || language
  const [post, setPost] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [liked, setLiked] = useState(false)
  const [bookmarked, setBookmarked] = useState(false)

  useEffect(() => {
    // Find the post with the matching slug
    const foundPost = blogPosts.find((p) => p.slug === slug)

    if (foundPost) {
      // If post is marked as draft, redirect user to the blog index for current locale
      if ((foundPost as any).draft) {
        router.push(`/${language}/blog`)
        setLoading(false)
        return
      }
      setPost(foundPost)
    } else {
      // Redirect to blog page if post not found
      router.push(`/${language}/blog`)
    }

    setLoading(false)
  }, [slug, router]) // blogPosts is a stable top-level constant, no lint warning now

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl">Loading...</div>
      </div>
    )
  }

  if (!post) {
    return null
  }

  const postTitle = language === "en" ? post.title : post.titleEs || post.title
  const postContent = language === "en" ? post.content : post.contentEs || post.content
  const authorBio = language === "en" ? post.authorBio : post.authorBioEs || post.authorBio
  // Use locale-aware featured image
  const postImage = post.slug === "digital-twin-abstract-data-3d-web-apps"
    ? (language === "en" ? "/images/blog/featured/digital-twin-en.png" : "/images/blog/featured/digital-twin-es.png")
    : post.image

  return (
    <>
      <Header />
  <main className="main-content pb-16">
        {/* Hero Section */}
        <div className="relative h-[50vh] min-h-[400px] w-full bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 opacity-40">
            <img src={postImage || "/placeholder.svg"} alt={postTitle} className="w-full h-full object-cover" />
          </div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>

          {/* Content */}
          <div className="container mx-auto px-6 relative z-10 h-full flex flex-col justify-end pb-12">
            <Button
              variant="ghost"
              size="sm"
              className="text-white mb-6 w-fit hover:bg-white/20"
              onClick={() => router.back()}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <Badge className="mb-4 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 w-fit">
              {post.category}
            </Badge>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 max-w-4xl">{postTitle}</h1>

            <div className="flex flex-wrap items-center gap-4 text-white/80">
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="/images/authors/davidxavila-profile-picture.png" alt={post.author} />
                  <AvatarFallback>DA</AvatarFallback>
                </Avatar>
                <span>{post.author}</span>
              </div>

              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{new Date(post.date).toLocaleDateString(routeLocale, { year: 'numeric', month: '2-digit', day: '2-digit' })}</span>
              </div>

              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-8">
              <article className="prose prose-lg dark:prose-invert max-w-none">
                <div dangerouslySetInnerHTML={{ __html: postContent }} />
              </article>

              {/* Tags */}
              <div className="mt-12">
                <h3 className="text-lg font-semibold mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag: string) => (
                    <Badge key={tag} variant="outline" className="text-sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Engagement */}
              <div className="mt-12 flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <Button
                    variant={liked ? "default" : "outline"}
                    size="sm"
                    className={liked ? "bg-sky-500 hover:bg-sky-600" : ""}
                    onClick={() => setLiked(!liked)}
                  >
                    <ThumbsUp className="mr-2 h-4 w-4" />
                    {liked ? "Liked" : "Like"} ({post.likes + (liked ? 1 : 0)})
                  </Button>

                  <Button variant="outline" size="sm">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Comments ({post.comments})
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => setBookmarked(!bookmarked)}>
                    <Bookmark className={`h-5 w-5 ${bookmarked ? "fill-current" : ""}`} />
                  </Button>

                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
                    <Share2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Author Bio */}
              <div className="mt-12 p-6 bg-muted rounded-lg">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src="/images/authors/davidxavila-profile-picture.png" alt={post.author} />
                    <AvatarFallback>DA</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-semibold">{post.author}</h3>
                    <p className="text-medium-contrast">{authorBio}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-8">
                {/* Table of Contents */}
                <div className="p-6 bg-card rounded-lg border">
                  <h3 className="text-lg font-semibold mb-4">{blogT('tableOfContents')}</h3>
                  <nav className="space-y-2">
                    <a href="#manufacturers-configurator" className="block text-sm text-medium-contrast hover:text-high-contrast">
                      1. {blogT('tocItems.manufacturers')}
                    </a>
                    <a href="#real-estate-portal" className="block text-sm text-medium-contrast hover:text-high-contrast">
                      2. {blogT('tocItems.realEstate')}
                    </a>
                    <a href="#research-accelerator" className="block text-sm text-medium-contrast hover:text-high-contrast">
                      3. {blogT('tocItems.researchers')}
                    </a>
                    <a href="#conclusion" className="block text-sm text-medium-contrast hover:text-high-contrast">
                      {blogT('tocItems.conclusion')}
                    </a>
                  </nav>
                </div>

                {/* Share */}
                <div className="p-6 bg-card rounded-lg border">
                  <h3 className="text-lg font-semibold mb-4">{blogT('shareArticle')}</h3>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 bg-transparent" aria-label="Share on X">
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 bg-transparent" aria-label="Share on LinkedIn">
                      <Linkedin className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 bg-transparent" aria-label="Share on Facebook">
                      <Facebook className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Newsletter */}
                <NewsletterSubscribe className="p-6 bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-lg border" />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  )
}
